import React, { useState, useCallback } from 'react';
import { Calculator, ToggleLeft, ToggleRight, Info } from 'lucide-react';
import { QueueingSystemParams, InputMode } from '../types';
import { QueueingCalculator } from '../utils/queueingCalculations';

interface InputPanelProps {
  params: QueueingSystemParams;
  onParamsChange: (params: QueueingSystemParams) => void;
  onCalculate: () => void;
  isCalculating?: boolean;
}

export const InputPanel: React.FC<InputPanelProps> = ({
  params,
  onParamsChange,
  onCalculate,
  isCalculating = false
}) => {
  const [inputMode, setInputMode] = useState<InputMode>('direct-cv');

  const handleInputChange = useCallback((field: keyof QueueingSystemParams, value: number) => {
    const newParams = { ...params, [field]: value };
    
    // Auto-calculate CV values when in mean-stddev mode
    if (inputMode === 'mean-stddev') {
      if (field === 'arrivalMean' || field === 'arrivalStdDev') {
        const mean = field === 'arrivalMean' ? value : (newParams.arrivalMean || 0);
        const stdDev = field === 'arrivalStdDev' ? value : (newParams.arrivalStdDev || 0);
        newParams.arrivalCV = QueueingCalculator.calculateCVFromMeanStd(mean, stdDev);
        newParams.arrivalRate = mean;
      }
      if (field === 'serviceMean' || field === 'serviceStdDev') {
        const mean = field === 'serviceMean' ? value : (newParams.serviceMean || 0);
        const stdDev = field === 'serviceStdDev' ? value : (newParams.serviceStdDev || 0);
        newParams.serviceCV = QueueingCalculator.calculateCVFromMeanStd(mean, stdDev);
        newParams.serviceRate = mean;
      }
    }

    onParamsChange(newParams);
  }, [params, onParamsChange, inputMode]);

  const toggleInputMode = useCallback(() => {
    const newMode: InputMode = inputMode === 'direct-cv' ? 'mean-stddev' : 'direct-cv';
    setInputMode(newMode);
    
    // Sync the parameters when switching modes
    if (newMode === 'mean-stddev') {
      const newParams = {
        ...params,
        arrivalMean: params.arrivalRate,
        arrivalStdDev: QueueingCalculator.calculateStdFromMeanCV(params.arrivalRate, params.arrivalCV),
        serviceMean: params.serviceRate,
        serviceStdDev: QueueingCalculator.calculateStdFromMeanCV(params.serviceRate, params.serviceCV)
      };
      onParamsChange(newParams);
    }
  }, [inputMode, params, onParamsChange]);

  return (
    <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-100">System Parameters</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <Info className="w-4 h-4" />
          <span>G/G/m Model</span>
        </div>
      </div>

      {/* Input Mode Toggle */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-300 mb-3">Input Mode</label>
        <button
          onClick={toggleInputMode}
          className="flex items-center justify-between w-full max-w-sm bg-dark-700 rounded-lg p-1 border border-dark-600"
        >
          <span className={`flex-1 text-center py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 ${
            inputMode === 'direct-cv' 
              ? 'bg-primary-600 text-white' 
              : 'text-gray-400 hover:text-gray-200'
          }`}>
            Direct CV
          </span>
          <span className={`flex-1 text-center py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 ${
            inputMode === 'mean-stddev' 
              ? 'bg-primary-600 text-white' 
              : 'text-gray-400 hover:text-gray-200'
          }`}>
            Mean/Std Dev
          </span>
        </button>
      </div>

      {/* Arrival Process Section */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
          <div className="w-2 h-2 bg-accent-400 rounded-full mr-2"></div>
          Arrival Process
        </h3>
        
        {inputMode === 'direct-cv' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Arrival Rate (r<sub>a</sub>) - items/hour
              </label>
              <input
                type="number"
                value={params.arrivalRate}
                onChange={(e) => handleInputChange('arrivalRate', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Coefficient of Variation (CV<sub>a</sub>)
              </label>
              <input
                type="number"
                value={params.arrivalCV}
                onChange={(e) => handleInputChange('arrivalCV', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mean Rate (μ<sub>ra</sub>) - items/hour
              </label>
              <input
                type="number"
                value={params.arrivalMean || params.arrivalRate}
                onChange={(e) => handleInputChange('arrivalMean', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Standard Deviation (σ<sub>ra</sub>)
              </label>
              <input
                type="number"
                value={params.arrivalStdDev || QueueingCalculator.calculateStdFromMeanCV(params.arrivalRate, params.arrivalCV)}
                onChange={(e) => handleInputChange('arrivalStdDev', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
            <div className="bg-dark-900 rounded-lg p-3 border border-dark-600">
              <span className="text-sm text-gray-400">Calculated CV<sub>a</sub>: </span>
              <span className="text-sm font-mono text-primary-400">
                {QueueingCalculator.formatNumber(params.arrivalCV)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Service Process Section */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
          <div className="w-2 h-2 bg-primary-400 rounded-full mr-2"></div>
          Service Process
        </h3>
        
        {inputMode === 'direct-cv' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Service Rate (r<sub>p</sub>) - items/hour
              </label>
              <input
                type="number"
                value={params.serviceRate}
                onChange={(e) => handleInputChange('serviceRate', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Coefficient of Variation (CV<sub>p</sub>)
              </label>
              <input
                type="number"
                value={params.serviceCV}
                onChange={(e) => handleInputChange('serviceCV', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mean Rate (μ<sub>rp</sub>) - items/hour
              </label>
              <input
                type="number"
                value={params.serviceMean || params.serviceRate}
                onChange={(e) => handleInputChange('serviceMean', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Standard Deviation (σ<sub>rp</sub>)
              </label>
              <input
                type="number"
                value={params.serviceStdDev || QueueingCalculator.calculateStdFromMeanCV(params.serviceRate, params.serviceCV)}
                onChange={(e) => handleInputChange('serviceStdDev', parseFloat(e.target.value) || 0)}
                className="input-field"
                step="0.1"
                min="0"
              />
            </div>
            <div className="bg-dark-900 rounded-lg p-3 border border-dark-600">
              <span className="text-sm text-gray-400">Calculated CV<sub>p</sub>: </span>
              <span className="text-sm font-mono text-primary-400">
                {QueueingCalculator.formatNumber(params.serviceCV)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* System Configuration */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
          <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
          System Configuration
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Number of Servers (m)
          </label>
          <input
            type="number"
            value={params.servers}
            onChange={(e) => handleInputChange('servers', parseInt(e.target.value) || 1)}
            className="input-field"
            min="1"
            step="1"
          />
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={onCalculate}
        disabled={isCalculating}
        className="btn-primary w-full flex items-center justify-center space-x-2"
      >
        <Calculator className={`w-5 h-5 ${isCalculating ? 'animate-spin' : ''}`} />
        <span>{isCalculating ? 'Calculating...' : 'Calculate & Update'}</span>
      </button>

      {/* Quick Presets */}
      <div className="mt-6 pt-6 border-t border-dark-600">
        <h4 className="text-sm font-medium text-gray-300 mb-3">Quick Presets</h4>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onParamsChange({
              arrivalRate: 18, serviceRate: 25, arrivalCV: 1, serviceCV: 1, servers: 1
            })}
            className="btn-secondary text-xs py-2"
          >
            M/M/1
          </button>
          <button
            onClick={() => onParamsChange({
              arrivalRate: 36, serviceRate: 25, arrivalCV: 2, serviceCV: 0.4, servers: 2
            })}
            className="btn-secondary text-xs py-2"
          >
            High Var
          </button>
          <button
            onClick={() => onParamsChange({
              arrivalRate: 15, serviceRate: 20, arrivalCV: 0.5, serviceCV: 0.3, servers: 1
            })}
            className="btn-secondary text-xs py-2"
          >
            Low Var
          </button>
          <button
            onClick={() => onParamsChange({
              arrivalRate: 50, serviceRate: 15, arrivalCV: 1.5, serviceCV: 0.8, servers: 4
            })}
            className="btn-secondary text-xs py-2"
          >
            Multi-Server
          </button>
        </div>
      </div>
    </div>
  );
};